import React, {useEffect, useRef, useState} from 'react';
import {Controller, SubmitHandler, useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {useSelector, useDispatch} from 'react-redux';
import ReCAPTCHA from 'react-google-recaptcha';
import {RootState} from '../../store';
import {debounce, getPasswordStrength} from "../../util/helper-methods";
import {PersonalSignUpFormData, StrengthObjType} from "../../util/interfaces";
import {
  defaultMessageObj,
  defaultSiteKey,
  defaultStrengthObj,
  isTest,
} from "../../util/constants";
import {
  CaptchaLegal,
  CheckboxCopy, Form, NameContainer, NameInput,
  StyledA, StyledLink, StyledField, SubmitButton, CaptchaContainer
} from "styles/styled";
import {PasswordInput, TextInput, Checkbox} from "shared-react";
import PasswordStrength from "../password-strength/password-strength";
import styled from "styled-components";
import {AuthEffects} from "../../store/auth";
import MessageCard from "../message-card";
import {Routes} from "../../routes";

const Spacer = styled.div `{
  min-width: 30px;
}`;

const schema = yup.object().shape({
  givenName: yup.string().required('First name is required'),
  familyName: yup.string().required('Last name is required').min(2, 'A valid last name is required.'),
  email: yup.string().email('A valid email is required').required('Email is a required field'),
  password: yup.string(),
  agreedToTOSandPP: yup.boolean().test(
    'agreedToTOSandPP',
    'Please agree to the Terms of Service and Privacy Policy.',
    value => value === true
  ),
  agreedToMarketingCommunications: yup.boolean(),
});

const PersonalSignup = React.forwardRef(() => {

  const dispatch = useDispatch();
  const [passwordStrength, setPasswordStrength] = useState<StrengthObjType>(defaultStrengthObj);
  const captchaDisabled = useSelector((state: RootState) => state.auth.captchaDisabled);
  const captchaKey = useSelector((state: RootState) => state.auth.captchaKey);
  const signupErrorMessage = useSelector((state: RootState) => state.auth.signupErrorMessage);
  const queryParams = useSelector((state: RootState) => state.app.queryParams);
  const urlExtension = useSelector((state: RootState) => state.app.urlExtension);
  let recaptchaRef = useRef<ReCAPTCHA>(null);
  const messageRef = useRef<HTMLDivElement>(null);
  const { register, handleSubmit, control, setValue, formState: { errors } } = useForm(
    {
      resolver: yupResolver(schema),
      mode: 'onSubmit',
      reValidateMode: 'onChange'
    });

  useEffect(() => {
    // they query params are not always parsed at render time, so this logic must be added to useEffect
    const {firstName, lastName, email} = queryParams;
    // forced to do it this way because poynt passes givenName and familyName back as firstName and Last Name
    firstName && setValue('givenName', decodeURIComponent(firstName));
    lastName && setValue('familyName', decodeURIComponent(lastName));
    email && setValue('email', decodeURIComponent(email));
  }, [queryParams, setValue]);

  useEffect(() => {
    if (signupErrorMessage && messageRef.current) {
      messageRef.current.scrollIntoView({behavior: 'smooth'});
    }
  }, [signupErrorMessage]);

  const onSubmit: SubmitHandler<PersonalSignUpFormData> = async (data:PersonalSignUpFormData ) => {
    let token: string | null = null;
    if(!captchaDisabled && recaptchaRef.current){
      token = await recaptchaRef.current.executeAsync();
      recaptchaRef.current.reset();
    }

    dispatch(AuthEffects.startPersonalSignUp({...data, gCaptchaResponse: token}));
  };

  // using this to overwrite the change handler of password
  const passwordChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue('password', event.target.value);
    determineStrength(event);
  }

  const determineStrength = (event: React.ChangeEvent<HTMLInputElement>) => {
    const temp = getPasswordStrength(event.target.value);
    setPasswordStrength(temp);
  }

  let displayErrorMessage
  if(!signupErrorMessage){
    displayErrorMessage = null;
  } else {
    switch (signupErrorMessage) {
      case 'user with that email already exists':
        displayErrorMessage = <span>This email address has already been used. Already have an account? <StyledLink aria-label="login" role="link" to={Routes.LOGIN + urlExtension}>Login</StyledLink>.</span>
        break;
      case 'Rate Limited':
        displayErrorMessage =
          <span>You have tried to sign up too many times in a short period of time. Please contact <StyledA
            aria-label="email support" href="mailto:support@bitpay.com">support</StyledA> if you're having trouble with our signup process.</span>;
        break;
      default:
        displayErrorMessage = <span>Account creation failed.</span>
        break;
    }
  }

  return (
    <>
      <div ref={messageRef}>
        <MessageCard type={defaultMessageObj.type}>
          {displayErrorMessage}
        </MessageCard>
      </div>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <NameContainer>
          <StyledField errorMessage={errors.givenName?.message} error={errors.givenName} label="First Name">
            <NameInput aria-label="First Name" autoComplete="disable" {...register('givenName')}/>
          </StyledField>
          <Spacer/>
          <StyledField errorMessage={errors.familyName?.message} error={errors.familyName} label="Last Name">
            <NameInput aria-label="Last Name" autoComplete="disable" {...register('familyName')}/>
          </StyledField>
        </NameContainer>
        <StyledField errorMessage={errors.email?.message} error={errors.email} label="Email Address">
          <TextInput aria-label="Email" type="email" autoComplete="disable" readOnly={!!queryParams.email} styleDisabled={!!queryParams.email} {...register('email')}/>
        </StyledField>
        <StyledField label="Password">
          <PasswordInput aria-label="Password" type="password" autoComplete="new-password" {...register('password')} onChange={debounce(passwordChangeHandler)}/>
        </StyledField>
        {passwordStrength.description && <PasswordStrength color={passwordStrength.color} value={passwordStrength.value} copy={passwordStrength.description as string}/>}
        <StyledField errorMessage={errors.agreedToTOSandPP?.message} error={errors.agreedToTOSandPP}>
          <Controller name="agreedToTOSandPP" control={control}
                      render={({ field: { onChange, ref } }) => {

                        return <Checkbox aria-label="Agree to Terms of Service and Privacy Policy" onChange={onChange} ref={ref}/>

                      }} />
          <CheckboxCopy>I agree to the <StyledA href="/legal/terms-of-use/#bitpay-id-terms-of-use" target="_blank">BitPay ID Terms of Use</StyledA> and <StyledA href="/about/privacy/" target="_blank">Privacy Policy</StyledA>.</CheckboxCopy>
        </StyledField>

        <StyledField>
          <Controller name="agreedToMarketingCommunications" control={control}
                      render={({ field: { onChange, ref } }) => {

                        return <Checkbox aria-label="Yes, I would like to receive promotional emails from BitPay." onChange={onChange} ref={ref}/>

                      }} />
          <CheckboxCopy>Yes, I would like to receive promotional emails from BitPay.</CheckboxCopy>
        </StyledField>
        <CaptchaContainer>
          {!captchaDisabled &&
          <ReCAPTCHA
            sitekey={ isTest ? defaultSiteKey : captchaKey}
            ref={recaptchaRef}
            size="invisible"
            badge="inline"
          />}
        </CaptchaContainer>
        <SubmitButton type="submit" role="button" aria-label="submit" disabled={passwordStrength.value < 75} >Submit</SubmitButton>
        {!captchaDisabled && <CaptchaLegal>
          This site is protected by reCAPTCHA and the Google
          <StyledA
            href="https://policies.google.com/privacy"
            target="_blank"
            rel="noreferrer noopener"
          > Privacy Policy </StyledA>
          and
          <StyledA
            href="https://policies.google.com/terms"
            target="_blank"
            rel="noreferrer noopener"
          > Terms of Service </StyledA>
          apply.
        </CaptchaLegal>}
      </Form>
    </>
  )
});

export default PersonalSignup;
